import _ from "lodash";
import React, { useState } from "react";
import { Dialog } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import {
  ADD_MEMBERS_OPTION_LIST,
  isChatToChannelEnabled,
  CHAT_TYPE_ENUM,
} from "MessagingV2/modules/Utils";
import { createCustomChannelAndMoveMessages } from "MessagingV2/modules/MessagingModule";
import ChatToChannelInfoModal from "./ChatToChannelInfoModal";
import AddMembersModal from "./AddMembersModal";
import PreviewChatModal from "./PreviewChatModal";
import ManageMembersModal from "./ManageMembersModal";
import AddMemberOrCreateChannelOptionModal from "./AddMemberOrCreateChannelOptionModal";
import PreviewExistingChannelsModal from "./PreviewExistingChannels";
import { RADIO_OPTIONS } from "./PreviewChatModal/PreviewChatWithOptions";
import SimpleAddMemberModal from "./SimpleAddMemberModal";

const MODALS = {
  INFO_MODAL: "INFO_MODAL",
  ADD_MEMBERS_MODAL: "ADD_MEMBERS_MODAL",
  PREVIEW_CHAT_MODAL: "PREVIEW_CHAT_MODAL",
  EXISTING_CHANNEL_MODAL: "EXISTING_CHANNEL_MODAL",
  MANAGE_MEMBERS_MODAL: "MANAGE_MEMBERS_MODAL",
  ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL:
    "ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL",
  ADD_MEMBERS_TO_NEW_CHANNEL_MODAL: "ADD_MEMBERS_TO_NEW_CHANNEL_MODAL",
  SIMPLE_ADD_MEMBER_MODAL: "SIMPLE_ADD_MEMBER_MODAL",
};

const getInitialModal = ({
  isCustomChannelType,
  isDepartmentChannelType,
  showAddMemberModalOnly,
}) => {
  if (isCustomChannelType) {
    return showAddMemberModalOnly
      ? MODALS.MANAGE_MEMBERS_MODAL
      : MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL;
  }
  if (isDepartmentChannelType) {
    return MODALS.SIMPLE_ADD_MEMBER_MODAL;
  }

  return MODALS.INFO_MODAL;
};

const ChatToChannel = ({
  onCloseModal,
  chatId,
  conversationType,
  channelLabel,
  users = [],
  allSelected = false,
}) => {
  const dispatch = useDispatch();
  const isCustomChannelType = _.includes(
    [CHAT_TYPE_ENUM.CHANNEL_CUSTOM, CHAT_TYPE_ENUM.CHANNEL_PUBLIC],
    conversationType
  );

  const isDepartmentChannelType = _.isEqual(
    conversationType,
    CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT
  );
  const showAddMemberModalOnly =
    isDepartmentChannelType || !isChatToChannelEnabled();

  const [modal, setModal] = useState(() =>
    getInitialModal({
      isCustomChannelType,
      isDepartmentChannelType,
      showAddMemberModalOnly,
    })
  );

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(users);
  const [existingInfo, setExistingChannelCount] = useState({
    existingChannelCount: 0,
    includesMessages: false,
  });

  const { t } = useI18n(["common"]);

  const [previewSelectedState, setPreviewSelectedState] = useState({
    option: RADIO_OPTIONS.NO_CONVERSATION_HISTORY,
    date: null,
  });
  const [selectedOption, setSelectedOption] = useState(null);

  const goToAddMemberModal = () => setModal(MODALS.ADD_MEMBERS_MODAL);
  const goToManageMemberModal = () => setModal(MODALS.MANAGE_MEMBERS_MODAL);
  const goToAddMembersToNewChannelModal = () =>
    setModal(MODALS.ADD_MEMBERS_TO_NEW_CHANNEL_MODAL);

  const onPreviewExistingModalBack = () => {
    if (isCustomChannelType) {
      goToAddMembersToNewChannelModal();
      return;
    }
    goToAddMemberModal();
  };

  const goToPreviewChatModal = () => {
    if (existingInfo.includesMessages) {
      setModal(MODALS.PREVIEW_CHAT_MODAL);
    } else {
      onCreateChatToChannel();
    }
  };

  const isSelectedUsersUpdated =
    !_.isEqual(_.size(selectedUsers), _.size(users)) &&
    !_.every(users, user => _.some(selectedUsers, ["id", user.id]));

  const goToInfoModal = () => setModal(MODALS.INFO_MODAL);

  const goToOptionModal = () =>
    setModal(MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL);

  const goToExistingChannelModal = () =>
    setModal(MODALS.EXISTING_CHANNEL_MODAL);

  const handleGoBackForAddMembersModal = () => {
    if (showAddMemberModalOnly) {
      onCloseModal();
    } else {
      goToOptionModal();
    }
  };

  const onClickHandler = () => {
    if (!selectedOption) return;

    switch (selectedOption) {
      case _.get(_.first(ADD_MEMBERS_OPTION_LIST), "value"):
        goToManageMemberModal();
        return;
      case _.get(ADD_MEMBERS_OPTION_LIST[1], "value"):
        goToAddMembersToNewChannelModal();
        return;
    }
  };

  const onCloseClick = (showWarning = true) => {
    if (isCustomChannelType) {
      showWarning &&
      (isSelectedUsersUpdated ||
        !_.isEqual(modal, MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL))
        ? setConfirmDialog(true)
        : onCloseModal();
    }
    if (isDepartmentChannelType) {
      isSelectedUsersUpdated ? setConfirmDialog(true) : onCloseModal();
    } else {
      isSelectedUsersUpdated || !_.isEqual(modal, MODALS.INFO_MODAL)
        ? setConfirmDialog(true)
        : onCloseModal();
    }
  };

  const onCloseDialogConfirm = () => {
    setConfirmDialog(false);
    onCloseModal();
  };

  const onCloseDialogCancel = () => setConfirmDialog(false);

  const onCreateChatToChannel = async () => {
    const allUserIds = _.map(selectedUsers, "id");
    const userIds = _.difference(allUserIds, _.map(users, "id"));
    const dateFrom = _.get(previewSelectedState, "date");
    return dispatch(
      createCustomChannelAndMoveMessages({
        userIds,
        chatId,
        dateFrom,
      })
    );
  };

  const handleRemoveUsers = users => {
    const userIds = _.map(users, "id");
    setSelectedUsers(state => {
      return _.filter(state, user => !_.includes(userIds, user.id));
    });
  };

  const handleAddUsers = users => {
    setSelectedUsers(state => {
      const userList = _.map(users, user => user);
      const allUsers = [...state, ...userList];
      return _.uniqBy(allUsers, "id");
    });
  };

  const handleAddMembersNextClick = response => {
    const existingChannelCount = _.get(
      response,
      "data.node.chats.totalCount",
      0
    );
    const includesMessages = !_.isEmpty(
      _.get(response, "data.node.messages.edges", [])
    );

    setExistingChannelCount({ existingChannelCount, includesMessages });

    if (_.gt(existingChannelCount, 0)) {
      goToExistingChannelModal();
    } else if (includesMessages) {
      setModal(MODALS.PREVIEW_CHAT_MODAL);
    }
  };

  const handlePreviewChatBackClick = () => {
    if (_.gt(existingInfo.existingChannelCount, 0)) {
      goToExistingChannelModal();
    } else {
      if (isCustomChannelType) {
        goToAddMembersToNewChannelModal();
      } else {
        goToAddMemberModal();
      }
    }
  };

  return (
    <>
      {_.isEqual(modal, MODALS.ADD_MEMBERS_OR_CREATE_CHANNEL_OPTION_MODAL) ? (
        <AddMemberOrCreateChannelOptionModal
          onClose={onCloseClick}
          onClickNext={onClickHandler}
          onSelectOption={setSelectedOption}
          selectedOption={selectedOption}
        />
      ) : null}
      {_.isEqual(modal, MODALS.ADD_MEMBERS_TO_NEW_CHANNEL_MODAL) ? (
        <AddMembersModal
          key={MODALS.ADD_MEMBERS_TO_NEW_CHANNEL_MODAL}
          onClose={onCloseClick}
          onBackClick={goToOptionModal}
          handleRemoveUsers={handleRemoveUsers}
          handleAddUsers={handleAddUsers}
          title={t("common:add_members_to_label", { label: `${channelLabel}` })}
          onClickNext={handleAddMembersNextClick}
          onCreateChatToChannel={onCreateChatToChannel}
          previousUsers={users}
          chatId={chatId}
          selectedUsers={selectedUsers}
        />
      ) : null}
      {_.isEqual(modal, MODALS.INFO_MODAL) ? (
        <ChatToChannelInfoModal
          onClose={onCloseClick}
          onClickNext={goToAddMemberModal}
          conversationType={conversationType}
        />
      ) : null}
      {_.isEqual(modal, MODALS.ADD_MEMBERS_MODAL) ? (
        <AddMembersModal
          onClose={onCloseClick}
          onBackClick={goToInfoModal}
          handleRemoveUsers={handleRemoveUsers}
          handleAddUsers={handleAddUsers}
          onClickNext={handleAddMembersNextClick}
          selectedUsers={selectedUsers}
          previousUsers={users}
          chatId={chatId}
          onCreateChatToChannel={onCreateChatToChannel}
          title={
            _.isEqual(conversationType, CHAT_TYPE_ENUM.CHAT_PERSONAL)
              ? t("common:add_members_to_conversation")
              : t("common:add_members_to_new_channel")
          }
        />
      ) : null}
      {_.isEqual(modal, MODALS.EXISTING_CHANNEL_MODAL) ? (
        <PreviewExistingChannelsModal
          onClose={onCloseClick}
          channelCount={existingInfo.existingChannelCount}
          onBackClick={onPreviewExistingModalBack}
          onClickNext={goToPreviewChatModal}
          userIds={_.map(selectedUsers, "id")}
        />
      ) : null}
      {_.isEqual(modal, MODALS.PREVIEW_CHAT_MODAL) ? (
        <PreviewChatModal
          onClose={onCloseClick}
          onBackClick={handlePreviewChatBackClick}
          onClickNext={onCreateChatToChannel}
          selectedState={previewSelectedState}
          setSelectedState={setPreviewSelectedState}
          chatId={chatId}
        />
      ) : null}
      {_.isEqual(modal, MODALS.MANAGE_MEMBERS_MODAL) ? (
        <ManageMembersModal
          onCloseModal={onCloseClick}
          onBackClick={handleGoBackForAddMembersModal}
          channelLabel={channelLabel}
          channelType={conversationType}
          prevUsers={users}
          chatId={chatId}
        />
      ) : null}
      {_.isEqual(modal, MODALS.SIMPLE_ADD_MEMBER_MODAL) ? (
        <SimpleAddMemberModal
          onCloseModal={onCloseClick}
          channelLabel={channelLabel}
          channelType={conversationType}
          prevUsers={users}
          chatId={chatId}
          allSelected={allSelected}
        />
      ) : null}
      {confirmDialog ? (
        <Dialog
          dsVersion="2.0"
          onClose={onCloseDialogCancel}
          title={t("common:are_you_sure")}
          message={t("common:chat_to_channel_dialog_discard_message")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:discard")}
          variant="delete-dialog"
          onButtonOneClick={onCloseDialogCancel}
          onButtonTwoClick={onCloseDialogConfirm}
        />
      ) : null}
    </>
  );
};

export default ChatToChannel;
